import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  Truck, 
  Wrench, 
  Settings, 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  CheckCircle,
  Star,
  ArrowRight,
  Menu,
  X
} from 'lucide-react';
import { mockData } from '../data/mock';

const HomePage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // SEO: Atualizar título da página baseado na seção
  useEffect(() => {
    document.title = 'Mardhi Adequações - Especialistas em Peças para Caminhões | Serra/ES';
    
    // Meta description dinâmica
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'A Mardhi Adequações é especializada em peças para motores, sistemas hidráulicos e componentes para caminhões em Serra/ES. Qualidade, confiabilidade e atendimento personalizado.');
    }
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const openWhatsApp = () => {
    const phoneNumber = '5527996939202'; // Brasil + DDD 27 + número sem formatação
    const message = 'Oi, gostaria de mais informações sobre produtos e serviços da Mardhi';
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-md z-50 border-b border-amber-200" role="banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="bg-blue-900 p-2 rounded-lg">
                <Truck className="h-6 w-6 text-amber-400" aria-hidden="true" />
              </div>
              <span className="text-xl font-bold text-blue-900">Mardhi Adequações</span>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8" role="navigation" aria-label="Menu principal">
              <button 
                onClick={() => scrollToSection('home')}
                className="text-blue-800 hover:text-blue-900 transition-colors duration-200"
                aria-label="Ir para seção inicial"
              >
                Início
              </button>
              <button 
                onClick={() => scrollToSection('about')}
                className="text-blue-800 hover:text-blue-900 transition-colors duration-200"
                aria-label="Ir para seção sobre a empresa"
              >
                Sobre
              </button>
              <button 
                onClick={() => scrollToSection('services')}
                className="text-blue-800 hover:text-blue-900 transition-colors duration-200"
                aria-label="Ir para seção de serviços"
              >
                Serviços
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="text-blue-800 hover:text-blue-900 transition-colors duration-200"
                aria-label="Ir para seção de contato"
              >
                Contato
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-amber-100 transition-colors"
              aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X className="h-6 w-6 text-blue-900" aria-hidden="true" /> : <Menu className="h-6 w-6 text-blue-900" aria-hidden="true" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <nav className="md:hidden border-t border-amber-200 py-4" role="navigation" aria-label="Menu mobile">
              <div className="flex flex-col space-y-4">
                <button 
                  onClick={() => scrollToSection('home')}
                  className="text-left text-blue-800 hover:text-blue-900 transition-colors"
                >
                  Início
                </button>
                <button 
                  onClick={() => scrollToSection('about')}
                  className="text-left text-blue-800 hover:text-blue-900 transition-colors"
                >
                  Sobre
                </button>
                <button 
                  onClick={() => scrollToSection('services')}
                  className="text-left text-blue-800 hover:text-blue-900 transition-colors"
                >
                  Serviços
                </button>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="text-left text-blue-800 hover:text-blue-900 transition-colors"
                >
                  Contato
                </button>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <main role="main">
        <section id="home" className="pt-24 pb-16 px-4 sm:px-6 lg:px-8" itemScope itemType="https://schema.org/Organization">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-4xl mx-auto">
              <Badge className="mb-6 bg-amber-100 text-blue-900 hover:bg-amber-200">
                Especialistas em Soluções para Caminhões
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold text-blue-900 mb-6" itemProp="name">
                  Peças e Soluções Hidráulicas
              </h1>
              <p className="text-xl text-blue-800 mb-8 leading-relaxed" itemProp="description">
                Somos especialistas em tomadas de força, para-lamas e suportes, mangueiras hidráulicas prensadas, kits, bombas e conexões hidráulicas. Garantimos qualidade, confiabilidade e um atendimento personalizado para atender às necessidades da sua frota.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  onClick={() => scrollToSection('services')}
                  className="bg-amber-500 hover:bg-amber-600 text-blue-900 px-8 py-3 group font-semibold"
                  aria-label="Conhecer serviços da Mardhi Adequações"
                >
                  Conheça Nossos Serviços
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={openWhatsApp}
                  className="border-blue-900 text-blue-900 hover:bg-blue-50 px-8 py-3"
                  aria-label="Entrar em contato via WhatsApp"
                >
                  Entre em Contato
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-20 bg-white" itemScope itemType="https://schema.org/AboutPage">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
                Sobre a Mardhi Adequações
              </h2>
              <p className="text-lg text-blue-800 max-w-2xl mx-auto" itemProp="about">
                {mockData.company.description}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-blue-900 mb-6">Nossa Experiência</h3>
                <div className="space-y-6">
                  {mockData.company.features.map((feature, index) => (
                    <article key={index} className="flex items-start space-x-4" itemScope itemType="https://schema.org/Service">
                      <div className="bg-amber-100 p-2 rounded-full flex-shrink-0">
                        <CheckCircle className="h-5 w-5 text-amber-600" aria-hidden="true" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-blue-900 mb-1" itemProp="name">{feature.title}</h4>
                        <p className="text-blue-800" itemProp="description">{feature.description}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
              
              <aside className="bg-amber-50 p-8 rounded-2xl border border-amber-200">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center" itemScope itemType="https://schema.org/QuantitativeValue">
                    <div className="text-3xl font-bold text-blue-900 mb-2" itemProp="value">{mockData.stats.experience}</div>
                    <div className="text-blue-800" itemProp="name">Anos de Experiência</div>
                  </div>
                  <div className="text-center" itemScope itemType="https://schema.org/QuantitativeValue">
                    <div className="text-3xl font-bold text-blue-900 mb-2" itemProp="value">{mockData.stats.customers}</div>
                    <div className="text-blue-800" itemProp="name">Clientes Atendidos</div>
                  </div>
                  <div className="text-center" itemScope itemType="https://schema.org/QuantitativeValue">
                    <div className="text-3xl font-bold text-blue-900 mb-2" itemProp="value">{mockData.stats.parts}</div>
                    <div className="text-blue-800" itemProp="name">Tipos de Peças</div>
                  </div>
                  <div className="text-center" itemScope itemType="https://schema.org/QuantitativeValue">
                    <div className="text-3xl font-bold text-blue-900 mb-2" itemProp="value">{mockData.stats.satisfaction}</div>
                    <div className="text-blue-800" itemProp="name">Satisfação</div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-20 bg-amber-50" itemScope itemType="https://schema.org/Service">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
                Nossos Serviços
              </h2>
              <p className="text-lg text-blue-800 max-w-2xl mx-auto">
                Especializados em peças de alta qualidade para manter seu caminhão sempre em movimento
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {mockData.services.map((service, index) => {
                const Icon = service.icon === 'Settings' ? Settings : 
                            service.icon === 'Wrench' ? Wrench : 
                            Truck;
                
                return (
                  <article key={index} className="hover:shadow-lg transition-shadow duration-300 border-0 shadow-sm bg-white" itemScope itemType="https://schema.org/Service">
                    <Card>
                      <CardHeader className="text-center pb-6">
                        <div className="mx-auto bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                          <Icon className="h-8 w-8 text-amber-600" aria-hidden="true" />
                        </div>
                        <CardTitle className="text-xl text-blue-900" itemProp="name">{service.title}</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <p className="text-blue-800 text-center mb-6" itemProp="description">{service.description}</p>
                        <ul className="space-y-2" itemProp="offers" itemScope itemType="https://schema.org/Offer">
                          {service.items.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-center space-x-2">
                              <CheckCircle className="h-4 w-4 text-amber-500 flex-shrink-0" aria-hidden="true" />
                              <span className="text-sm text-blue-800">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20 bg-white" itemScope itemType="https://schema.org/ContactPage">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
                Entre em Contato
              </h2>
              <p className="text-lg text-blue-800 max-w-2xl mx-auto">
                Estamos prontos para atender suas necessidades. Entre em contato conosco!
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              <address className="space-y-8 not-italic" itemScope itemType="https://schema.org/LocalBusiness">
                <div className="flex items-start space-x-4">
                  <div className="bg-amber-100 p-3 rounded-full">
                    <MapPin className="h-6 w-6 text-amber-600" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">Endereço</h4>
                    <p className="text-blue-800" itemProp="address">{mockData.contact.address}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-amber-100 p-3 rounded-full">
                    <Phone className="h-6 w-6 text-amber-600" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">Telefones</h4>
                    <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-1 sm:space-y-0">
                      <p className="text-blue-800">
                        <a href={`tel:${mockData.contact.phone.replace(/\D/g, '')}`} itemProp="telephone">
                          {mockData.contact.phone}
                        </a>
                      </p>
                      <p className="text-blue-800">
                        <a href={`tel:${mockData.contact.phone2.replace(/\D/g, '')}`} itemProp="telephone">
                          {mockData.contact.phone2}
                        </a>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-amber-100 p-3 rounded-full">
                    <Mail className="h-6 w-6 text-amber-600" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">Email</h4>
                    <p className="text-blue-800">
                      <a href={`mailto:${mockData.contact.email}`} itemProp="email">
                        {mockData.contact.email}
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-amber-100 p-3 rounded-full">
                    <Clock className="h-6 w-6 text-amber-600" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">Horário de Funcionamento</h4>
                    <p className="text-blue-800" itemProp="openingHours">{mockData.contact.hours}</p>
                  </div>
                </div>
              </address>

              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-amber-200">
                <div className="p-6 bg-amber-50 border-b border-amber-200">
                  <h3 className="text-xl font-semibold text-blue-900 mb-2">Nossa Localização</h3>
                  <p className="text-blue-800">Encontre-nos facilmente</p>
                </div>
                <div className="relative h-96">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3740.8925648!2d-40.2969742!3d-20.1279651!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xb81f2d39c09809%3A0x9b090196987ccc5b!2sMardhi%20Adequa%C3%A7%C3%B5es!5e0!3m2!1spt!2sbr!4v1754532796583!5m2!1spt!2sbr"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="absolute inset-0"
                    title="Localização da Mardhi Adequações - Jardim Limoeiro, Serra/ES"
                  />
                  <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-lg border border-amber-200">
                    <p className="text-sm font-medium text-blue-900">Mardhi Adequações</p>
                    <p className="text-xs text-blue-800">Jardim Limoeiro - Serra/ES</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-blue-900 text-white py-12" role="contentinfo" itemScope itemType="https://schema.org/Organization">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="bg-amber-400 p-2 rounded-lg">
                <Truck className="h-6 w-6 text-blue-900" aria-hidden="true" />
              </div>
              <span className="text-xl font-bold" itemProp="name">Mardhi Adequações</span>
            </div>
            <div className="text-amber-100 text-center md:text-right">
              <p>&copy; 2025 Mardhi Adequações. Todos os direitos reservados.</p>
              <p className="mt-1" itemProp="description">Especialistas em peças para caminhões</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;